import { apiRequest } from '@/lib/api/client'
import type { LandingAchievementId } from '@/lib/landing-achievements'
import type { ScratchCardConfig } from '@/lib/campaign-scratch-card'
import type { MonthlyMedalId } from '@/lib/monthly-medals'

export type AchievementMessage = {
  target_days?: number
  enabled?: boolean
  rewards?: AchievementRewards
  title: string
  text: string | null
  video_url: string | null
  locked_reason?: string | null
}

export type AchievementRewards = {
  coupon_percent: number | null
  lucky_numbers: number
  roulette_spins: number
}

export const EMPTY_ACHIEVEMENT_REWARDS: AchievementRewards = {
  coupon_percent: null,
  lucky_numbers: 0,
  roulette_spins: 0,
}

export type CampaignGamification = {
  campaign_id: number
  special_supporter_target_amount?: string
  achievements: Record<LandingAchievementId, AchievementMessage> &
    Partial<Record<MonthlyMedalId, AchievementMessage>>
  scratch_card?: ScratchCardConfig
}

export type ListableAchievementId = Exclude<
  LandingAchievementId,
  'first_purchase' | 'scratch_card'
>

export type CampaignAchievementRecipients = {
  campaign_id: number
  achievement_id: ListableAchievementId
  donors: { id: number; name: string; email: string | null; phone: string | null }[]
  pagination: { current_page: number; last_page: number; per_page: number; total: number }
}

export function fetchCampaignAchievementRecipients(
  id: number,
  badge: ListableAchievementId,
  page: number,
  token?: string,
  signal?: AbortSignal
) {
  return apiRequest<CampaignAchievementRecipients>(
    `/api/campaigns/${id}/gamification/${badge}/recipients?page=${page}`,
    {
      requireAuth: true,
      token,
      signal,
      cache: 'no-store',
    }
  )
}

export function fetchCampaignGamification(id: number, token?: string) {
  return apiRequest<CampaignGamification>(`/api/campaigns/${id}/gamification`, { token })
}

export function updateCampaignGamification(
  id: number,
  achievements: CampaignGamification['achievements'],
  token?: string,
  specialSupporterTargetAmount?: string,
  scratchCard?: ScratchCardConfig
) {
  return apiRequest<CampaignGamification>(`/api/campaigns/${id}/gamification`, {
    method: 'PUT',
    body: JSON.stringify({
      achievements,
      special_supporter_target_amount: specialSupporterTargetAmount,
      scratch_card: scratchCard,
    }),
    token,
  })
}

export function updateCampaignSpecialSupporterTarget(
  id: number,
  amount: string,
  token?: string
) {
  return apiRequest<CampaignGamification>(`/api/campaigns/${id}/gamification`, {
    method: 'PUT',
    body: JSON.stringify({ special_supporter_target_amount: amount }),
    token,
  })
}
